// The home's context-menu tip (pages/main.js CtxMenuTip) and the keyboard way
// into the same menus (persona audit round 1, WH-9 — seven personas).
//
// MEASURED (dev 2026-10-24/25): the tip floated bottom-right over the cards
// and the chat on every page — it stayed up over an open window, the
// assistant and the welcome — it never went away unless its × was found, and
// it named no keyboard way ("the hint is mouse-only").
//
//   1. ONCE. It is dismissed for good after it has been SEEN for a few
//      seconds — the same dismissal its × writes (`_homeCtxTipDismissed` +
//      the `smbls_pref__homeCtxTipDismissed` cookie app.js reads at boot).
//      Time spent hidden does not count.
//   2. NEVER OVER CONTENT. It shows only on the plain board: not while a
//      window (PageSlot) is open, nor the assistant, the main thread, the
//      welcome or customize mode — the layers it used to sit on top of.
//   3. THE KEYBOARD WAY. Shift+F10 or the context-menu key on the board opens
//      the menu a right-click opens, on every OS (macOS has no keyboard
//      context menu of its own; Windows and Linux send their own, which is
//      cancelled so one menu opens, not two). The tip's words name it.
//
// FRANK: each export is self-contained (FA204); `this` is the calling element.

// Is the tip allowed on screen now? Pure over the root state.
export function homeCtxTipVisible(root) {
  const r = root && typeof root === 'object' ? root : null
  if (!r) return false
  return !(
    r._homeCtxTipDismissed ||
    r._homeCustomizeMode ||
    r._canvasSlot ||
    r._canvasSlotSecond ||
    r._homeThreadOpen ||
    r.assistantOpen ||
    r._onboardingOpen
  )
}

// The once-timer. `tip` is the tip's element (its `scope` holds the timer);
// call it with the tip's visibility whenever that changes (mount, a state
// change, removal → false). It counts SEEN time only: hiding pauses it,
// showing again resumes with what is left. When it runs out the tip is
// dismissed for good. `env` is for tests (a clock and timers).
export function homeCtxTipTick(tip, visible, env) {
  const SEEN_MS = 8000
  const el = tip || this
  if (!el || typeof el !== 'object') return false
  const e = env && typeof env === 'object' ? env : {}
  const now = typeof e.now === 'function' ? e.now : () => Date.now()
  const set = typeof e.setTimeout === 'function' ? e.setTimeout : (fn, ms) => setTimeout(fn, ms)
  const clear = typeof e.clearTimeout === 'function' ? e.clearTimeout : (id) => clearTimeout(id)
  const scope = el.scope && typeof el.scope === 'object' ? el.scope : (el.scope = {})
  const t = scope.ctxTipOnce || (scope.ctxTipOnce = { left: SEEN_MS, since: null, timer: null })
  if (t.done) return false
  if (!visible) {
    if (t.timer != null) {
      clear(t.timer)
      t.timer = null
      if (t.since != null) t.left = Math.max(0, t.left - (now() - t.since))
      t.since = null
    }
    return false
  }
  if (t.timer != null) return true
  t.since = now()
  t.timer = set(() => {
    t.timer = null
    t.done = true
    const root = el.state && el.state.root
    if (root && typeof root.update === 'function') {
      root.update({ _homeCtxTipDismissed: true }, { preventFetch: true })
    }
    try {
      if (typeof el.call === 'function') el.call('setPref', '_homeCtxTipDismissed', '1')
    } catch (_) {
      /* the cookie is a courtesy — the state already hides it */
    }
  }, t.left)
  return true
}

// Shift+F10 or the context-menu key on the board → the same `contextmenu`
// event a right-click sends, at the focused element, so the card's menu (or
// the board's) opens exactly as it does for the mouse. A text field keeps its
// own menu. → true when it opened one.
export function homeKeyboardContextMenu(e) {
  if (!e || e.defaultPrevented) return false
  const isMenuKey = e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey === true)
  if (!isMenuKey || e.altKey || e.ctrlKey || e.metaKey) return false
  const target = e.target
  if (!target || typeof target.dispatchEvent !== 'function') return false
  if (
    typeof target.closest === 'function' &&
    target.closest('input, textarea, [contenteditable]')
  ) {
    return false
  }
  const view = target.ownerDocument && target.ownerDocument.defaultView
  if (!view || typeof view.MouseEvent !== 'function') return false
  const r =
    typeof target.getBoundingClientRect === 'function'
      ? target.getBoundingClientRect()
      : { left: 0, top: 0, width: 0, height: 0 }
  if (typeof e.preventDefault === 'function') e.preventDefault()
  target.dispatchEvent(
    new view.MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      clientX: Math.round(r.left + Math.min(r.width / 2, 24)),
      clientY: Math.round(r.top + Math.min(r.height / 2, 24)),
      view
    })
  )
  return true
}
